import {
  Box,
  Grid,
  Toolbar,
  Typography,
  FormControl,
  Select,
  MenuItem,
  CircularProgress,
  Button,
  Pagination,
  TextField,
  Card,
  CardContent,
  InputAdornment,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Divider,
  Stack,
  Chip,
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "../../../utils/Api";
import { toast } from "react-toastify";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { format } from "date-fns";
import { CloudDownloadOutlined } from "@mui/icons-material";
import SearchRounded from "@mui/icons-material/SearchRounded";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import jsPDF from "jspdf";
import "jspdf-autotable";

function ConsumptionReport() {
  const token = localStorage.getItem("token");
  const userRole = localStorage.getItem("role");
  const outletData = JSON.parse(localStorage.getItem("outletData") || "{}");

  const [searchText, setSearchText] = useState("");
  const [loading, setLoading] = useState(false);

  // Filters
  const [selectedCategory, setSelectedCategory] = useState("");
  const [selectedMaterial, setSelectedMaterial] = useState("");

  // DatePicker expects Date objects
  const [selectedStartDate, setSelectedStartDate] = useState(new Date());
  const [selectedEndDate, setSelectedEndDate] = useState(new Date());

  // Dropdown categories (from current stock api)
  const [categories, setCategories] = useState([]);

  // Report rows
  const [reportRows, setReportRows] = useState([]);

  const itemsPerPage = 50;
  const [pagination, setPagination] = useState({
    totalDocuments: 0,
    limit: itemsPerPage,
    NumberOfPages: 1,
    currentPage: 1,
  });
  const [currentPage, setCurrentPage] = useState(1);

  // ---------- CATEGORY LIST ----------
  const getRawMaterialsList = async () => {
    try {
      const response = await axios.get(
        `${API.BASE_URL}stock-transactions/getOutletCurrentStock`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params: { outletId: outletData?._id, limit: 100 },
        }
      );

      const cats = response?.data?.data?.rawMaterialCategories || [];
      setCategories(Array.isArray(cats) ? cats : []);
    } catch (error) {
      console.error("Error:", error);
      setCategories([]);
    }
  };

  const uniqueCategories = useMemo(() => {
    const rows = Array.isArray(categories) ? categories : [];
    return rows.filter(
      (category, index, self) =>
        index ===
        self.findIndex(
          (c) => c?.mainCategory?._id === category?.mainCategory?._id
        )
    );
  }, [categories]);

  // Category -> Materials list (optional if available in categories response)
  const materialsForSelectedCategory = useMemo(() => {
    const rows = Array.isArray(categories) ? categories : [];
    if (!selectedCategory) return rows;
    return rows.filter((c) => c?.mainCategory?._id === selectedCategory);
  }, [categories, selectedCategory]);

  // ---------- REPORT API CALL ----------
  const getStockTransactions = async (page = 1) => {
    if (!outletData?._id) return;

    // ✅ date validation
    if (
      selectedStartDate &&
      selectedEndDate &&
      selectedStartDate > selectedEndDate
    ) {
      toast.error("From Date cannot be greater than To Date", {
        autoClose: 3000,
      });
      return;
    }

    setLoading(true);
    try {
      const startDateStr = format(selectedStartDate, "yyyy-MM-dd");
      const endDateStr = format(selectedEndDate, "yyyy-MM-dd");

      const params = {
        startDate: startDateStr,
        endDate: endDateStr,
        outletId: outletData?._id,
        page,
        limit: itemsPerPage,
      };

      if (selectedCategory) params.mainCategory = selectedCategory;
      if (selectedMaterial) params.rawMaterialId = selectedMaterial;

      const response = await axios.get(
        `${API.BASE_URL}stock-transactions/getDebitOutletStockTransactions`,
        {
          headers: { Authorization: `Bearer ${token}` },
          params,
        }
      );

      // ✅ handle "status: false" safely (like your other report)
      const resData = response?.data;
      if (resData?.status === false) {
        setReportRows([]);
        setPagination({
          totalDocuments: 0,
          limit: itemsPerPage,
          NumberOfPages: 1,
          currentPage: 1,
        });
        setCurrentPage(1);

        if (resData?.data?.message) {
          toast.info(resData?.data?.message, { autoClose: 2500 });
        }
        return;
      }

      const preview = resData?.data?.rawmaterialPreviewData || [];
      const pag = resData?.data?.paginationRawData || {};

      setReportRows(Array.isArray(preview) ? preview : []);

      setPagination({
        totalDocuments: pag?.totalDocuments ?? (Array.isArray(preview) ? preview.length : 0),
        limit: pag?.limit ?? itemsPerPage,
        NumberOfPages: pag?.NumberOfPages ?? 1,
        currentPage: pag?.currentPage ?? page,
      });

      setCurrentPage(pag?.currentPage ?? page);
    } catch (error) {
      console.error("Error:", error);
      setReportRows([]);
      toast.error("Failed to fetch report", { autoClose: 3000 });
    } finally {
      setLoading(false);
    }
  };

  // ---------- PDF EXPORT ----------
  const generatePDF = () => {
    const rowsSafe = Array.isArray(filteredRows) ? filteredRows : [];
    if (rowsSafe.length === 0) {
      toast.info("No rows to export", { autoClose: 2000 });
      return;
    }

    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    const title = "Stock Consumption Report (Summary)";
    const textWidth = doc.getTextWidth(title);
    const titleX = (pageWidth - textWidth) / 2;

    doc.setFontSize(16);
    doc.text(title, titleX, 20);

    doc.setFontSize(10);
    const outletName = outletData?.name || "Unknown Outlet";
    const outletAddress = outletData?.address || "No Address Available";
    const outletContact = `${outletData?.email || "No Email"}, ${
      outletData?.phone || "No Phone Info"
    }`;

    doc.text(`Outlet: ${outletName}`, 14, 30);
    doc.text(`Address: ${outletAddress}`, 14, 40);
    doc.text(`Contact: ${outletContact}`, 14, 50);

    const startDateStr = format(selectedStartDate, "yyyy-MM-dd");
    const endDateStr = format(selectedEndDate, "yyyy-MM-dd");
    doc.text(`From: ${startDateStr}  To: ${endDateStr}`, 14, 60);

    const columns = [
      { header: "#", dataKey: "itemNo" },
      { header: "Raw Material", dataKey: "rawMaterial" },
      { header: "Category", dataKey: "mainCategory" },
      { header: "Sub Category", dataKey: "subCategory" },
      { header: "HSN", dataKey: "hsn" },
      { header: "Tax %", dataKey: "taxPercent" },
      { header: "Type", dataKey: "type" },
      { header: "Qty", dataKey: "quantity" },
      { header: "Unit", dataKey: "unit" },
    ];

    const rows = rowsSafe.map((r, index) => ({
      itemNo: index + 1,
      rawMaterial: r.rawMaterial,
      mainCategory: r.mainCategory,
      subCategory: r.subCategory,
      hsn: r.hsn,
      taxPercent: r.taxPercent,
      type: r.type,
      quantity: Number(r.quantity || 0).toFixed(3),
      unit: r.unit,
    }));

    doc.autoTable({
      columns,
      body: rows,
      startY: 70,
      theme: "grid",
      styles: { halign: "center", fontSize: 8 },
      headStyles: { halign: "center" },
    });

    const today = new Date();
    const fileDate = today.toISOString().split("T")[0];
    doc.save(`${fileDate}-Stock-Consumption-Report.pdf`);
  };

  // ---------- EVENTS ----------
  const handleCategoryChange = (event) => {
    const categoryId = event.target.value;
    setSelectedCategory(categoryId);
    setSelectedMaterial(""); // reset material when category changes
    setCurrentPage(1);
  };

  const handleMaterialChange = (event) => {
    setSelectedMaterial(event.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (event, page) => {
    setCurrentPage(page);
    getStockTransactions(page);
  };

  // ---------- SEARCH + FILTER ----------
  const filteredRows = useMemo(() => {
    const rows = Array.isArray(reportRows) ? reportRows : [];
    const q = searchText.trim().toLowerCase();
    if (!q) return rows;

    return rows.filter((r) => {
      const rm = (r?.rawMaterial || "").toLowerCase();
      const mc = (r?.mainCategory || "").toLowerCase();
      const sc = (r?.subCategory || "").toLowerCase();
      const hsn = String(r?.hsn || "").toLowerCase();
      const type = (r?.type || "").toLowerCase();
      return (
        rm.includes(q) ||
        mc.includes(q) ||
        sc.includes(q) ||
        hsn.includes(q) ||
        type.includes(q)
      );
    });
  }, [reportRows, searchText]);

  const legendTotals = useMemo(() => {
    const rows = Array.isArray(filteredRows) ? filteredRows : [];
    return rows.reduce(
      (acc, r) => {
        acc.totalQuantity += Number(r?.quantity || 0);
        acc.totalRows += 1;
        return acc;
      },
      { totalRows: 0, totalQuantity: 0 }
    );
  }, [filteredRows]);

  useEffect(() => {
    getRawMaterialsList();
    // initial load (optional)
    // getStockTransactions(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Box className="dashBoard" sx={{ background: "#f6f8fb", minHeight: "100vh" }}>
      <Toolbar />

      <Grid container justifyContent="center" sx={{ pb: 6 }}>
        <Grid item xs={12} md={11}>
          {/* Header */}
          <Card
            elevation={0}
            sx={{
              mt: 4,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "linear-gradient(135deg, #ffffff 0%, #f8fbff 100%)",
            }}
          >
            <CardContent sx={{ py: 3 }}>
              <Stack
                direction={{ xs: "column", md: "row" }}
                alignItems={{ xs: "flex-start", md: "center" }}
                justifyContent="space-between"
                spacing={2}
              >
                <Box>
                  <Typography sx={{ fontSize: 20, fontWeight: 800, color: "#0f172a" }}>
                    Stock Consumption Reports
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                    Filter by category, material and date range, then view or export the report.
                  </Typography>
                </Box>

                <Chip
                  icon={<FilterAltOutlined />}
                  label={`Records: ${filteredRows?.length || 0}`}
                  sx={{ fontWeight: 700, borderRadius: 2 }}
                  variant="outlined"
                />
              </Stack>
            </CardContent>
          </Card>

          {/* Filters */}
          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              background: "#fff",
            }}
          >
            <CardContent sx={{ py: 2 }}>
              <Grid container spacing={2} alignItems="center">
                {/* Category */}
                <Grid item xs={12} md={3}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Category
                  </Typography>

                  <FormControl
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52 } }}
                  >
                    <Select value={selectedCategory} onChange={handleCategoryChange} displayEmpty>
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>

                      {uniqueCategories?.map((category) => (
                        <MenuItem
                          key={category?.mainCategory?._id}
                          value={category?.mainCategory?._id}
                        >
                          {category?.mainCategory?.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>

                {/* Material (optional dropdown - uses currentStock list) */}
                <Grid item xs={12} md={3}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Raw Material
                  </Typography>

                  <FormControl
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52 } }}
                  >
                    <Select
                      value={selectedMaterial}
                      onChange={handleMaterialChange}
                      displayEmpty
                      disabled={!materialsForSelectedCategory?.length}
                    >
                      <MenuItem value="">
                        <em>All</em>
                      </MenuItem>

                      {materialsForSelectedCategory?.map((m, idx) => (
                        <MenuItem key={m?._id || idx} value={m?.rawMaterialId || m?._id || ""}>
                          {m?.rawMaterialName || m?.rawMaterial || m?.name || "Raw Material"}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>

                  {/* NOTE:
                      If your raw material list does not exist in this API,
                      tell me your "rawMaterialCategories" object structure and I will map it correctly.
                  */}
                </Grid>

                {/* From Date */}
                <Grid item xs={12} md={2} sx={{ zIndex: 1000 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    From Date
                  </Typography>

                  <DatePicker
                    selected={selectedStartDate}
                    onChange={(date) => setSelectedStartDate(date)}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    customInput={
                      <TextField
                        fullWidth
                        value={selectedStartDate ? format(selectedStartDate, "yyyy-MM-dd") : ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            height: 52,
                            background: "#fff",
                          },
                        }}
                      />
                    }
                  />
                </Grid>

                {/* To Date */}
                <Grid item xs={12} md={2} sx={{ zIndex: 1000 }}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    To Date
                  </Typography>

                  <DatePicker
                    selected={selectedEndDate}
                    onChange={(date) => setSelectedEndDate(date)}
                    dateFormat="yyyy-MM-dd"
                    showYearDropdown
                    scrollableYearDropdown
                    yearDropdownItemNumber={100}
                    customInput={
                      <TextField
                        fullWidth
                        value={selectedEndDate ? format(selectedEndDate, "yyyy-MM-dd") : ""}
                        InputProps={{ readOnly: true }}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            borderRadius: 2,
                            height: 52,
                            background: "#fff",
                          },
                        }}
                      />
                    }
                  />
                </Grid>

                {/* Search */}
                <Grid item xs={12} md={3}>
                  <Typography sx={{ fontSize: 12, fontWeight: 700, color: "#334155", mb: 0.8 }}>
                    Search
                  </Typography>

                  <TextField
                    fullWidth
                    placeholder="Search by raw material / category / HSN / type"
                    value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    sx={{
                      "& .MuiOutlinedInput-root": { borderRadius: 2, height: 52, background: "#fff" },
                    }}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchRounded />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                {/* Actions */}
                <Grid item xs={12} md={2}>
                  <Typography
                    sx={{
                      fontSize: 12,
                      fontWeight: 700,
                      color: "#334155",
                      mb: 0.8,
                      textAlign: "center",
                    }}
                  >
                    Actions
                  </Typography>

                  <Stack direction="row" justifyContent="space-between" spacing={1}>
                    <Button
                      variant="contained"
                      disabled={loading}
                      onClick={() => getStockTransactions(1)}
                      sx={{
                        height: 52,
                        width: "70%",
                        borderRadius: 2,
                        fontWeight: 800,
                        textTransform: "none",
                        backgroundColor: "#003049",
                        "&:hover": { backgroundColor: "#00263a" },
                      }}
                    >
                      {loading ? "Loading..." : "Get"}
                    </Button>

                    <CloudDownloadOutlined
                      onClick={generatePDF}
                      sx={{
                        fontSize: "35px",
                        cursor: filteredRows?.length ? "pointer" : "not-allowed",
                        color: filteredRows?.length ? "#003049" : "#94a3b8",
                        display: "flex",
                        alignSelf: "center",
                        pointerEvents: filteredRows?.length ? "auto" : "none",
                      }}
                      titleAccess="Download PDF"
                    />
                  </Stack>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Content */}
          <Card
            elevation={0}
            sx={{
              mt: 2,
              borderRadius: 3,
              border: "1px solid #e6eaf0",
              overflow: "hidden",
              background: "#fff",
            }}
          >
            <CardContent sx={{ p: 0 }}>
              {loading ? (
                <Box sx={{ py: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 2 }}>
                  <CircularProgress />
                  <Typography sx={{ color: "#334155", fontWeight: 700 }}>Fetching Report...</Typography>
                </Box>
              ) : filteredRows?.length === 0 ? (
                <Box sx={{ py: 6, textAlign: "center" }}>
                  <Typography sx={{ fontSize: 16, fontWeight: 800, color: "#0f172a" }}>
                    No consumption report to display
                  </Typography>
                  <Typography sx={{ mt: 0.5, fontSize: 13, color: "#64748b" }}>
                    Select filters and click “Get” to view results.
                  </Typography>
                </Box>
              ) : (
                <>
                  {/* Legend */}
                  <Box sx={{ px: 2, py: 1.5, borderBottom: "1px solid #e6eaf0", background: "#fff" }}>
                    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" alignItems="center" justifyContent="space-between">
                      <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
                        <Chip
                          icon={<FilterAltOutlined />}
                          label={`Rows: ${legendTotals.totalRows}`}
                          variant="outlined"
                          sx={{ fontWeight: 800, borderRadius: 2 }}
                        />
                        <Chip
                          label={`Total Qty: ${legendTotals.totalQuantity.toFixed(3)}`}
                          variant="outlined"
                          sx={{ fontWeight: 800, borderRadius: 2 }}
                        />
                      </Stack>

                      <Chip
                        label={`Outlet: ${outletData?.name || "Outlet"}`}
                        sx={{
                          fontWeight: 900,
                          borderRadius: 2,
                          backgroundColor: "#003049",
                          color: "#fff",
                          "& .MuiChip-label": { color: "#fff" },
                        }}
                      />
                    </Stack>
                  </Box>

                  {/* Table */}
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      borderRadius: 0,
                      maxHeight: "70vh",
                      overflow: "auto",
                    }}
                  >
                    <Table stickyHeader>
                      <TableHead>
                        <TableRow>
                          {[
                            "No.",
                            "Raw Material",
                            "Category",
                            "Sub Category",
                            "HSN",
                            "Tax %",
                            "Type",
                            "Qty",
                            "Unit",
                          ].map((h) => (
                            <TableCell
                              key={h}
                              sx={{
                                fontWeight: 900,
                                color: "#0f172a",
                                background: "#f1f5f9",
                                borderBottom: "1px solid #e2e8f0",
                                whiteSpace: "nowrap",
                                position: "sticky",
                                top: 0,
                                zIndex: 2,
                              }}
                              align={h === "Raw Material" ? "left" : "center"}
                            >
                              {h}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>

                      <TableBody>
                        {filteredRows.map((row, index) => (
                          <TableRow
                            key={`${row?._id || index}`}
                            hover
                            sx={{
                              backgroundColor: index % 2 === 0 ? "#ffffff" : "#EDF9FF",
                              "& td": { borderBottom: "1px solid #f1f5f9" },
                            }}
                          >
                            <TableCell align="center" sx={{ fontWeight: 700, color: "#334155" }}>
                              {index + 1}
                            </TableCell>

                            <TableCell align="left">
                              <Typography sx={{ fontSize: 14, fontWeight: 900, color: "#0f172a", textTransform: "capitalize" }}>
                                {row?.rawMaterial || "N/A"}
                              </Typography>

                              {row?.storageLocation && row?.storageLocation !== "N/A" && (
                                <Typography sx={{ fontSize: 11, color: "#475569", mt: 0.3 }}>
                                  <b>Storage:</b> {row?.storageLocation}
                                </Typography>
                              )}
                            </TableCell>

                            <TableCell align="center" sx={{ fontWeight: 700, textTransform: "capitalize" }}>
                              {row?.mainCategory || "N/A"}
                            </TableCell>

                            <TableCell align="center" sx={{ fontWeight: 700, textTransform: "capitalize" }}>
                              {row?.subCategory || "N/A"}
                            </TableCell>

                            <TableCell align="center" sx={{ fontWeight: 700 }}>
                              {row?.hsn || "N/A"}
                            </TableCell>

                            <TableCell align="center" sx={{ fontWeight: 700 }}>
                              {Number(row?.taxPercent || 0).toFixed(2)}
                            </TableCell>

                            <TableCell align="center" sx={{ fontWeight: 800, textTransform: "capitalize" }}>
                              {row?.type || "N/A"}
                            </TableCell>

                            <TableCell align="center" sx={{ fontWeight: 900 }}>
                              {Number(row?.quantity || 0).toFixed(3)}
                            </TableCell>

                            <TableCell align="center" sx={{ fontWeight: 700 }}>
                              {row?.unit || "N/A"}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  <Divider />

                  <Box sx={{ py: 2, display: "flex", justifyContent: "center" }}>
                    <Pagination
                      count={pagination?.NumberOfPages || 1}
                      page={currentPage}
                      onChange={handlePageChange}
                      color="primary"
                      variant="outlined"
                      shape="rounded"
                    />
                  </Box>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
}

export default ConsumptionReport;
